import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Button,
  IconButton,
  Flex,
  ModalProps,
  ModalContentProps,
  ModalBodyProps,
  ButtonProps,
} from "@chakra-ui/react";
import { isMobile } from "react-device-detect";
import SVG from "react-inlinesvg";
import CustomIcon from "../../../../public/icons-v1_1";

interface CustomModalProps extends Omit<ModalProps, "children"> {
  children: React.ReactNode;
  height?: string;
  minWidth?: string;
  maxWidth?: string;
  // scrollBehavior="inside"일 때 테마가 넣는 상한(calc(100% - 7.5rem))을 덮어야 하는 경우에만 넘긴다.
  maxHeight?: string;
}

const CustomApprovalModal = ({
  children,
  height,
  minWidth = "480px",
  maxWidth,
  maxHeight,
  ...props
}: CustomModalProps) => {
  return (
    // allowPinchZoom: Chakra의 스크롤 락(react-remove-scroll)이 두 손가락 제스처까지
    // 취소하기 때문에, 모바일에서 input 포커스로 화면이 확대되면 축소로 빠져나올 수 없다.
    // isMobile로 게이트하는 이유는 이 값이 데스크톱의 Ctrl+휠 줌 차단 여부까지 함께
    // 결정하기 때문이다. 무조건 true로 두면 데스크톱 기존 동작이 바뀐다.
    // {...props}보다 앞에 두어 호출부가 명시적으로 덮을 수 있게 한다.
    <Modal size={"sm"} allowPinchZoom={isMobile} {...props}>
      <ModalOverlay />
      <ModalContent
        minWidth={minWidth}
        height={height}
        maxHeight={maxHeight}
        borderRadius={"8px"}
        overflow={"hidden"}
        maxWidth={maxWidth}
      >
        {children}
      </ModalContent>
    </Modal>
  );
};

const Header = ({
  title,
  onClose,
}: {
  title: string | React.ReactNode;
  onClose?: () => void;
}) => {
  return (
    <ModalHeader
      background={"#F7F9FA"}
      padding={"12px 16px"}
      borderBottom={"0.5px solid #D4D4D1"}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        {typeof title === "string" ? (
          <Text textStyle={"body-md-bold"} color={"#444444"}>
            {title}
          </Text>
        ) : (
          title
        )}
        {onClose && (
          <IconButton
            aria-label="Close"
            icon={<CustomIcon src={"x"} width={28} height={28} />}
            variant={"ghost"}
            onClick={onClose}
            color={"#8F8F8C"}
            size={"sm"}
            _hover={{ background: "transparent" }}
          />
        )}
      </Flex>
    </ModalHeader>
  );
};

const Body = ({ children, ...props }: ModalBodyProps) => {
  return (
    <ModalBody padding={"16px"} {...props}>
      {typeof children === "string" ? (
        <Text textStyle={"body-md-regular"} color={"#444444"}>
          {children}
        </Text>
      ) : (
        children
      )}
    </ModalBody>
  );
};

const Footer = ({
  cancelName = "취소",
  confirmName = "확인",
  onCancel,
  onConfirm,
  isLoading,
  isConfirmDisabled,
  confirmBtnBgColor = "#1272EF",
  confirmBtnProps,
  leftContent,
}: {
  cancelName?: string;
  confirmName?: string;
  onCancel: () => void;
  onConfirm?: () => void;
  isLoading?: boolean;
  isConfirmDisabled?: boolean;
  confirmBtnBgColor?: string;
  confirmBtnProps?: ButtonProps;
  // 초기화 등 좌측 보조 액션 슬롯 — 넘기지 않으면 기존처럼 우측 정렬만 유지된다.
  leftContent?: React.ReactNode;
}) => {
  return (
    <ModalFooter
      padding={"12px 16px"}
      gap={"8px"}
      justifyContent={leftContent ? "space-between" : undefined}
    >
      {leftContent}
      <Flex gap={"12px"}>
        <Button
          role={"group"}
          flex={1}
          height={"28px"}
          width={"96px"}
          background={"#FCFCFB"}
          boxShadow={"0 0 0 0.5px #8F8F8C, 0 2px 4px 0 rgba(0, 0, 0, 0.10)"}
          borderRadius={"4px"}
          onClick={onCancel}
          isDisabled={isLoading}
          padding={"4px 8px"}
          _hover={{
            boxShadow: "0 0 0 0.5px #1272EF, 0 2px 4px 0 rgba(0, 0, 0, 0.10)",
          }}
          _active={{
            background: "inherit",
            boxShadow: "0 0 0 0.5px #00417D, 0 2px 4px 0 rgba(0, 0, 0, 0.10)",
          }}
        >
          <Text
            textStyle={"body-sm-bold"}
            color={"#444444"}
            _groupHover={{
              color: "#1272EF",
            }}
            _groupActive={{
              color: "#00417D",
            }}
          >
            {cancelName}
          </Text>
        </Button>
        {onConfirm && (
          <Button
            role={"group"}
            flex={1}
            height={"28px"}
            width={"96px"}
            background={
              isConfirmDisabled ? "#EFEFED" : confirmBtnBgColor ?? "#1272EF"
            }
            boxShadow={"0 0 0 0.5px #8F8F8C, 0 2px 4px 0 rgba(0, 0, 0, 0.10)"}
            borderRadius={"4px"}
            onClick={onConfirm}
            isLoading={isLoading}
            isDisabled={isConfirmDisabled}
            padding={"4px 8px"}
            _hover={{
              background: isConfirmDisabled ? "#E2E2E0" : "#005AAD",
            }}
            _active={{
              background: "#00417D",
            }}
            {...confirmBtnProps}
          >
            <Text
              textStyle={"body-sm-bold"}
              color={isConfirmDisabled ? "#C0C0C0" : "#FFFFFF"}
            >
              {confirmName}
            </Text>
          </Button>
        )}
      </Flex>
    </ModalFooter>
  );
};

CustomApprovalModal.Header = Header;
CustomApprovalModal.Body = Body;
CustomApprovalModal.Footer = Footer;

export default CustomApprovalModal;
